import { useState, useEffect, useCallback, useMemo } from 'react';
import { OperatingCost, calculateYearlyCost } from '../../types/operatingCosts';
import { operatingCostsApi } from '../../api/operatingCostsApi';

interface UseAllOperatingCostsReturn {
    costsByBuilding: { [buildingKey: string]: OperatingCost[] };
    loading: boolean;
    error: string | null;
    totalYearlyCostByBuilding: { [buildingKey: string]: number };
    totalYearlyCostByCategory: { [categoryKey: string]: number };
    grandTotalYearlyCost: number;
    refresh: () => Promise<void>;
}

export const useAllOperatingCosts = (): UseAllOperatingCostsReturn => {
    const [costsByBuilding, setCostsByBuilding] = useState<{ [buildingKey: string]: OperatingCost[] }>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Calculate total yearly cost per building
    const totalYearlyCostByBuilding = useMemo(() => {
        const totals: { [buildingKey: string]: number } = {};
        for (const [buildingKey, costs] of Object.entries(costsByBuilding)) {
            totals[buildingKey] = costs.reduce((total, cost) => {
                return total + calculateYearlyCost(cost.amount, cost.interval);
            }, 0);
        }
        return totals;
    }, [costsByBuilding]);

    // Calculate total yearly cost per category across all buildings
    const totalYearlyCostByCategory = useMemo(() => {
        const totals: { [categoryKey: string]: number } = {};
        for (const costs of Object.values(costsByBuilding)) {
            for (const cost of costs) {
                if (!totals[cost.categoryKey]) {
                    totals[cost.categoryKey] = 0;
                }
                totals[cost.categoryKey] += calculateYearlyCost(cost.amount, cost.interval);
            }
        }
        return totals;
    }, [costsByBuilding]);

    // Grand total across all buildings
    const grandTotalYearlyCost = useMemo(() => {
        return Object.values(totalYearlyCostByBuilding).reduce((sum, val) => sum + val, 0);
    }, [totalYearlyCostByBuilding]);

    // Load all costs
    const loadAllCosts = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const allCosts = await operatingCostsApi.getAllCosts();
            setCostsByBuilding(allCosts);
        } catch (err) {
            console.error('Error loading all operating costs:', err);
            setError('Kunde inte ladda driftkostnader');
        } finally {
            setLoading(false);
        }
    }, []);

    // Load on mount
    useEffect(() => {
        loadAllCosts();
    }, [loadAllCosts]);

    // Refresh
    const refresh = useCallback(async () => {
        await loadAllCosts();
    }, [loadAllCosts]);

    return {
        costsByBuilding,
        loading,
        error,
        totalYearlyCostByBuilding,
        totalYearlyCostByCategory,
        grandTotalYearlyCost,
        refresh,
    };
};
